import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Loader2 } from 'lucide-react';
import { Typography } from '../styles/primitives';
import { fetchPriceHistory, PriceHistoryPoint } from '../services/polymarketApi';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

interface EventPriceChartProps {
  tokenId: string;
  outcomeName: string;
}

const INTERVALS = [
  { label: '1H', value: '1h', fidelity: 1 },
  { label: '6H', value: '6h', fidelity: 5 },
  { label: '1D', value: '1d', fidelity: 15 },
  { label: '1W', value: '1w', fidelity: 60 },
  { label: '1M', value: '1m', fidelity: 360 },
  { label: 'ALL', value: 'max', fidelity: 1440 },
];

const EventPriceChart: React.FC<EventPriceChartProps> = ({ tokenId, outcomeName }) => {
  const [history, setHistory] = useState<PriceHistoryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeInterval, setActiveInterval] = useState('max');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const interval = INTERVALS.find(i => i.value === activeInterval) || INTERVALS[5];
      const data = await fetchPriceHistory(tokenId, activeInterval, interval.fidelity);
      if (!cancelled) {
        setHistory(data);
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [tokenId, activeInterval]);

  const formatLabel = (ts: number) => {
    const date = new Date(ts * 1000);
    if (activeInterval === '1h' || activeInterval === '6h') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (activeInterval === '1d') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const currentPrice = history.length > 0 ? history[history.length - 1].p : 0;
  const startPrice = history.length > 0 ? history[0].p : 0;
  const priceChange = currentPrice - startPrice;
  const isPositive = priceChange >= 0;

  const lineColor = isPositive ? '#10b981' : '#ef4444';

  const chartData = {
    labels: history.map(d => formatLabel(d.t)),
    datasets: [
      {
        data: history.map(d => d.p * 100),
        borderColor: lineColor,
        backgroundColor: isPositive
          ? 'rgba(16, 185, 129, 0.08)'
          : 'rgba(239, 68, 68, 0.06)',
        fill: true,
        tension: 0.2,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: lineColor,
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2,
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        backgroundColor: '#0f172a',
        titleColor: '#94a3b8',
        bodyColor: '#ffffff',
        titleFont: { size: 11 },
        bodyFont: { size: 13, weight: 'bold' as const },
        padding: 10,
        displayColors: false,
        callbacks: {
          title: (items: { label: string }[]) => items[0]?.label || '',
          label: (context: { parsed: { y: number } }) => `${context.parsed.y.toFixed(1)}%`,
        },
      },
      legend: { display: false },
    },
    scales: {
      x: {
        display: true,
        ticks: {
          maxTicksLimit: 6,
          color: '#94a3b8',
          font: { size: 11 },
        },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        display: true,
        min: 0,
        max: Math.max(
          ...history.map(d => d.p * 100),
          10
        ) * 1.15,
        ticks: {
          callback: (value: string | number) => `${value}%`,
          maxTicksLimit: 5,
          color: '#94a3b8',
          font: { size: 11 },
        },
        grid: {
          color: 'rgba(0,0,0,0.04)',
        },
        border: { display: false },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '14px',
        padding: '20px',
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <div>
          <span
            style={{
              fontSize: Typography.BodySmall,
              fontWeight: Typography.WeightSemibold,
              color: '#0f172a',
            }}
          >
            {outcomeName}
          </span>
          {history.length > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <span
                className="font-mono"
                style={{
                  fontSize: Typography.HeadlineMedium,
                  fontWeight: Typography.WeightBold,
                  color: '#0f172a',
                }}
              >
                {(currentPrice * 100).toFixed(1)}%
              </span>
              <span
                className="font-mono"
                style={{
                  fontSize: Typography.Caption,
                  fontWeight: Typography.WeightSemibold,
                  color: isPositive ? '#10b981' : '#ef4444',
                }}
              >
                {isPositive ? '+' : ''}{(priceChange * 100).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-1">
          {INTERVALS.map(interval => (
            <button
              key={interval.value}
              onClick={() => setActiveInterval(interval.value)}
              className="px-2.5 py-1.5 transition-colors"
              style={{
                fontSize: Typography.Fine,
                fontWeight:
                  activeInterval === interval.value
                    ? Typography.WeightBold
                    : Typography.WeightMedium,
                color: activeInterval === interval.value ? '#0f172a' : '#94a3b8',
                background:
                  activeInterval === interval.value
                    ? 'rgba(0,0,0,0.06)'
                    : 'transparent',
                borderRadius: '6px',
              }}
            >
              {interval.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ height: 220, position: 'relative', marginTop: '12px' }}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 size={24} className="animate-spin" style={{ color: '#94a3b8' }} />
          </div>
        ) : history.length > 0 ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <div className="flex items-center justify-center h-full">
            <span style={{ fontSize: Typography.Caption, color: '#94a3b8' }}>
              No price history available
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventPriceChart;
